import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import background from '../../assets/background.svg';
import config from '../../config';
import { toast } from 'react-toastify';
import { Button } from '../../components/Button';
import DotSpinner from '../../components/shared/DotSpinner';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { apiPayment } from '../../api/apiPayment.api';
import { apiTrials } from '../../api/apiTrials.api';
import { useSubscriptionTerms } from '../../components/terms/SubscriptionTerms';
import { useBindCard } from '../../hooks/useBindCard';

const CURRENCY_CONFIG = {
  BYN: {
    IconSmall: ({ className = '' }) => (
      <img src="/icons/BYN-small.svg" alt="currency" className={`w-[9px] ml-[4px] ${className}`} />
    )
  },
  STAR: {
    IconSmall: ({ className = '' }) => (
      <img src="/icons/tg_star.svg" alt="currency" className={`w-[14px] ml-[4px] ${className}`} />
    )
  }
};

const BENEFITS = [
  { icon: '/icons/paywall-card.svg', label: 'Лайки без ограничений' },
  { icon: '/icons/paywall-like.svg', label: 'Видите, кто вас лайкнул' },
  { icon: '/icons/paywall-superlike.svg', label: '5 супер-лайков в день' },
  { icon: '/icons/paywall-reload.svg', label: '5 возвратов в неделю' },
  { icon: '/icons/paywall-calendar.svg', label: 'Видите, кто идёт на мероприятие' },
  { icon: '/icons/paywall-gift.svg', label: 'Нет рекламы + подарок' }
];

function pluralDays(count) {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня';
  return 'дней';
}

function RadioCircle({ checked }) {
  return checked ? (
    <div className="w-[24px] h-[24px] flex items-center justify-center rounded-full border border-[#A1F69E] shrink-0">
      <div className="w-[12px] h-[12px] rounded-full bg-[#A1F69E]" />
    </div>
  ) : (
    <div className="w-[24px] h-[24px] rounded-full border border-[#A1F69E] shrink-0" />
  );
}

// Единственная карточка триала: заголовок — количество бесплатных дней,
// под ним стоимость продления, справа цена 0 рядом с перечёркнутой ценой покупки
function TrialRadioCard({ product, countDays, payment }) {
  const priceInfo = product.prices.find((p) => p.currency === payment);
  if (!priceInfo) return null;

  const { IconSmall } = CURRENCY_CONFIG[payment] || {};

  return (
    <div className="flex flex-row justify-between items-center w-full rounded-[16px] border border-[#A1F69E] bg-[#043939] p-3">
      <div className="flex flex-row items-center gap-[12px]">
        <RadioCircle checked={true} />
        <div className="flex flex-col">
          <span className="font-raleway font-bold text-white text-[18px]">
            {countDays} {pluralDays(countDays)} бесплатно
          </span>
          <span className="flex items-center gap-[4px] font-raleway text-[13px] font-semibold text-white opacity-70">
            После {Number(priceInfo.amount).toLocaleString('ru-RU')}
            <IconSmall className="!ml-0" />
            /мес
          </span>
        </div>
      </div>

      <div className="flex items-baseline gap-[16px] font-raleway text-white font-semibold">
        <span className="relative text-white text-[22px] opacity-50 text-center">
          <div className="ml-[5px] flex flex-row items-center justify-center">
            {Number(priceInfo.amount).toLocaleString('ru-RU')} <IconSmall />
          </div>
          <span className="absolute inset-0 w-full h-[2px] bg-red-500 rotate-[12deg] top-1/2 left-0"></span>
        </span>
        <span className="flex items-center text-[22px] leading-none">
          0
          <IconSmall />
        </span>
      </div>
    </div>
  );
}

export function SubscriptionTrial() {
  const { trialId } = useParams();

  const { CheckBox, isChecked, onAccept } = useSubscriptionTerms();

  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const [isLoading, setIsLoading] = useState(false);

  const bindCard = useBindCard();

  const disabledButton = isLoading || bindCard.isLoading || !isChecked;

  const [payment, setPayment] = useState('STAR');
  const [trial, setTrial] = useState(null);

  const countDays = trial?.countDays || 3;

  useEffect(() => {
    fetchTrialInitialState();
  }, [trialId]);

  const fetchTrialInitialState = async () => {
    if (!trialId) return;
    setIsLoading(true);
    try {
      await onAccept();
      await apiTrials.getTrialInfo(axiosPrivate, trialId).then((response) => setTrial(response));
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Ошибка при запросе подарка';
      toast.error(message);
      navigate(config.ROUTES.MEET);
    }
    setIsLoading(false);
  };

  const handleStartTrial = async () => {
    // Продление на рублёвую карту списывается с привязанной карты — проверяем её
    if (payment === 'BYN') {
      const needsBindCard = await apiPayment
        .list(axiosPrivate)
        .then((response) => !response.items.some((item) => item.isActive));

      if (needsBindCard) {
        bindCard.handleCreateBindCard();
        return toast.info('Необходимо привязать активную карту');
      }
    }

    try {
      await apiTrials.useTrial(axiosPrivate, trialId, payment);
      toast.success('Пробный период активирован!');
      navigate(config.ROUTES.SUBSCRIPTION.MAIN);
    } catch (error) {
      const message = error?.response?.data?.error || error.message;
      toast.error(message);
      navigate(config.ROUTES.MEET);
    }
  };

  if (isLoading || !trial) {
    return (
      <div className="flex items-center justify-center min-h-[100vh]">
        <DotSpinner />
      </div>
    );
  }

  return (
    <div className="relative w-[100%] h-[100%] overflow-x-hidden overflow-y-auto px-4 pt-[21px] pb-[180px]">
      <img src={background} alt="" className="absolute inset-0 w-full pointer-events-none" />

      <div className="relative z-1 flex flex-col">
        <button
          type="button"
          onClick={() => navigate(config.ROUTES.MEET)}
          className="ml-auto mb-[20px] block cursor-pointer"
        >
          <img alt="close" src="/icons/Button-close.svg" className="w-[44px] h-[44px]" />
        </button>

        <h1 className="text-[32px] font-bold text-[#A1F69E] font-raleway text-center">Mint premium</h1>

        <p className="text-[16px] text-white text-center font-raleway font-bold mb-[11px]">
          Вы найдёте свою любовь в разы быстрее
        </p>

        {/* Переключение валюты: у BYN и звёзд свои расценки */}
        <div className="w-full flex items-center justify-between bg-[#022424] p-[2px] rounded-[400px] font-raleway border border-[#233636] mb-[20px]">
          <div
            onClick={() =>
              config.BYN_PAYMENT_FROZEN ? toast.info(config.PURCHASES_FROZEN_MESSAGE) : setPayment('BYN')
            }
            className={` ${payment === 'BYN' ? 'bg-[#043939]' : 'bg-transparent'} w-1/2 h-[33px] rounded-[400px] flex justify-center items-center `}
          >
            <span className={`${payment === 'BYN' ? 'text-[#A1F69E]' : 'text-white'} text-[14px]`}>Деньги</span>
          </div>
          <div
            onClick={() => setPayment('STAR')}
            className={` ${payment === 'STAR' ? 'bg-[#043939]' : 'bg-transparent'} w-1/2 h-[33px] rounded-[400px] flex justify-center items-center`}
          >
            <span className={`${payment === 'STAR' ? 'text-[#A1F69E]' : 'text-white'} text-[14px]`}>
              Telegram звезды
            </span>
          </div>
        </div>

        {/* Единственный тариф триала */}
        <div className="flex flex-col gap-[12px] mb-[20px]">
          <TrialRadioCard product={trial.product} countDays={countDays} payment={payment} />
        </div>

        {/* Преимущества подписки */}
        <h2 className="font-raleway font-bold text-white text-[16px] mb-[12px]">Вы получаете с подпиской:</h2>
        <div className="grid grid-cols-2 gap-[12px] mb-[20px]">
          {BENEFITS.map((benefit) => (
            <div
              key={benefit.label}
              className="flex items-center gap-[10px] bg-[#010D0D] border border-[#022424] rounded-[12px] px-[12px] py-[14px]"
            >
              <img src={benefit.icon} alt="" className="w-[28px] h-[28px] shrink-0" />
              <span className="font-raleway text-white text-[13px] font-semibold leading-tight">
                {benefit.label}
              </span>
            </div>
          ))}
        </div>

        <p className="font-raleway text-[12px] text-white opacity-70 text-justify">
          Подписка будет продлеваться автоматически, если она не отменена как минимум за 24 часа до окончания текущего
          периода. Оплата спишется автоматически после окончания пробного периода, если он был оформлен. Отменить
          подписку можно в любой момент в настройках профиля
        </p>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-10 px-4 pb-[24px] pt-[16px] bg-gradient-to-t from-[#021A1A] via-[#021A1A] to-transparent">
        <Button
          onClick={handleStartTrial}
          className="w-full mb-[10px]"
          disabled={disabledButton}
        >
          {isLoading ? 'Загрузка...' : 'Начать бесплатный период'}
        </Button>

        <CheckBox />
      </div>
    </div>
  );
}
